import { makeAutoObservable, runInAction } from "mobx";

import { AdminMasterLeadFilter, AdminMasterLeadsService, IAdminMasterLead, MasterLeadStatus } from "./AdminMasterLeadsService";

export class AdminMasterLeadsStore {
  public Items: IAdminMasterLead[] = [];

  public Total = 0;
  public Limit = 20;
  public Offset = 0;

  public Status: AdminMasterLeadFilter = "";

  public IsLoading = false;
  public Error = "";

  public ChangingId: number | null = null;
  public ActionError = "";

  private requestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  public get Page(): number {
    return Math.floor(this.Offset / this.Limit) + 1;
  }

  public get TotalPages(): number {
    return Math.max(1, Math.ceil(this.Total / this.Limit));
  }

  public get CanGoPrev(): boolean {
    return this.Offset > 0 && !this.IsLoading;
  }

  public get CanGoNext(): boolean {
    return this.Offset + this.Limit < this.Total && !this.IsLoading;
  }

  public async load(): Promise<void> {
    const currentRequestId = ++this.requestId;

    this.IsLoading = true;
    this.Error = "";
    this.ActionError = "";

    try {
      const response = await AdminMasterLeadsService.GetList(this.Limit, this.Offset, this.Status);

      runInAction(() => {
        if (currentRequestId !== this.requestId) {
          return;
        }

        this.Items = response.items ?? [];
        this.Total = response.total ?? 0;

        this.Limit = response.limit > 0 ? response.limit : this.Limit;

        this.Offset = Math.max(0, response.offset ?? this.Offset);
      });
    } catch {
      runInAction(() => {
        if (currentRequestId !== this.requestId) {
          return;
        }

        this.Items = [];
        this.Total = 0;
        this.Error = "Не удалось загрузить заявки мастеров";
      });
    } finally {
      runInAction(() => {
        if (currentRequestId !== this.requestId) {
          return;
        }

        this.IsLoading = false;
      });
    }
  }

  public async setStatus(status: AdminMasterLeadFilter): Promise<void> {
    if (this.Status === status) return;

    this.Status = status;
    this.Offset = 0;
    this.Items = [];
    this.Total = 0;

    await this.load();
  }

  public async goPrevPage(): Promise<void> {
    if (!this.CanGoPrev) return;

    this.Offset = Math.max(0, this.Offset - this.Limit);

    await this.load();
  }

  public async goNextPage(): Promise<void> {
    if (!this.CanGoNext) return;

    this.Offset += this.Limit;

    await this.load();
  }

  public async changeStatus(id: number, status: MasterLeadStatus): Promise<boolean> {
    if (this.ChangingId !== null) {
      return false;
    }

    this.ChangingId = id;
    this.ActionError = "";

    try {
      const response = await AdminMasterLeadsService.ChangeStatus(id, status);

      if (!response.success) {
        throw new Error("master lead status change failed");
      }

      runInAction(() => {
        const item = this.Items.find((current) => current.id === id);

        if (item) {
          item.status = response.status;
          item.updatedAt = response.updatedAt;
        }
      });

      /*
        Если заявка после смены статуса исчезает
        из текущего фильтра, при единственном
        элементе возвращаемся на предыдущую
        страницу.
      */
      if (this.Status && response.status !== this.Status && this.Items.length === 1 && this.Offset > 0) {
        this.Offset = Math.max(0, this.Offset - this.Limit);
      }

      await this.load();

      return true;
    } catch {
      runInAction(() => {
        this.ActionError = "Не удалось изменить статус заявки";
      });

      return false;
    } finally {
      runInAction(() => {
        this.ChangingId = null;
      });
    }
  }
}
